// Staff authentication hooks using React Query (same pattern as admin)
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { queryKeys } from '@/lib/query-client'
import type { Staff } from '@/types/entities'

function getCachedStaff(): Staff | null {
  try {
    const raw = localStorage.getItem('digimataam_staff_auth')
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<Staff>
    // Minimal shape check
    if (
      typeof parsed !== 'object' ||
      !parsed ||
      typeof parsed.id !== 'string' ||
      typeof parsed.restaurantId !== 'string' ||
      typeof parsed.username !== 'string' ||
      (parsed.role !== 'server' && parsed.role !== 'kitchen' && parsed.role !== 'printer')
    ) {
      return null
    }
    return parsed as Staff
  } catch {
    return null
  }
}

/**
 * Get current authenticated staff member
 * Uses React Query for automatic caching and retry logic
 */
export function useCurrentStaff() {
  return useQuery({
    queryKey: queryKeys.auth.staff(),
    initialData: () => getCachedStaff(),
    queryFn: async (): Promise<Staff | null> => {
      const cached = getCachedStaff()

      // Prefer session (local) over getUser() (network) to avoid hanging on reload.
      const timeoutMs = 8000

      let authUserId: string | null = null
      try {
        // getSession is local; keep it simple and fast
        const { data } = await supabase.auth.getSession()
        authUserId = data.session?.user?.id ?? null
      } catch {
        // If auth is stuck recovering, fall back to cached staff so dashboards don't spin forever
        return cached
      }

      if (!authUserId) {
        return cached
      }

      // Fetch staff profile from database with restaurant info
      let staffResult: { data: any; error: any }
      try {
        const controller = new AbortController()
        const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs)
        try {
          // Supabase supports abortSignal at runtime, but our TS types don't expose it here.
          staffResult = await (supabase
            .from('staff')
            .select('*, restaurants:restaurant_id(id, name, address_ar, address_fr, phone)')
            .eq('id', authUserId)
            .maybeSingle() as any).abortSignal(controller.signal)
        } finally {
          window.clearTimeout(timeoutId)
        }
      } catch {
        return cached
      }

      const { data: staffData, error: staffError } = staffResult

      if (staffError) {
        console.error('[useCurrentStaff] Database error:', staffError)
        // Non-destructive fallback: avoid signing out on transient reload issues
        return cached
      }

      if (!staffData) {
        console.warn('[useCurrentStaff] Staff profile not found:', authUserId)
        return cached
      }

      // Check if staff is active
      if (!staffData.is_active || staffData.deleted_at) {
        console.warn('[useCurrentStaff] Staff is inactive or deleted:', authUserId)
        localStorage.removeItem('digimataam_staff_auth')
        return null
      }

      // Fetch staff zones
      let staffZones: any[] | null = null
      try {
        const controller = new AbortController()
        const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs)
        try {
          // Supabase supports abortSignal at runtime, but our TS types don't expose it here.
          const zonesRes = await (supabase
            .from('staff_zones')
            .select('zone_id')
            .eq('staff_id', authUserId) as any).abortSignal(controller.signal)
          staffZones = zonesRes.data ?? null
        } finally {
          window.clearTimeout(timeoutId)
        }
      } catch {
        // Zones are optional; continue with cached/no zones
        staffZones = null
      }

      const zoneIds = staffZones?.map((sz) => sz.zone_id) || []

      // Map restaurant data if available
      const restaurantData = staffData.restaurants
      const restaurant = restaurantData
        ? {
            id: restaurantData.id,
            name: restaurantData.name,
            addressAr: restaurantData.address_ar || null,
            addressFr: restaurantData.address_fr || null,
            phone: restaurantData.phone || null,
            uniqueIdentifier: null,
            tableCount: 0,
            isActive: true,
            allowNegativeStock: restaurantData.allow_negative_stock ?? false,
            createdBy: '',
            deletedAt: null,
            createdAt: '',
            updatedAt: '',
          }
        : undefined

      // Map to Staff entity
      const staff: Staff = {
        id: staffData.id,
        username: staffData.username,
        email: staffData.email,
        role: staffData.role as 'server' | 'kitchen' | 'printer',
        specialtyId: staffData.specialty_id,
        zoneId: zoneIds[0] || null, // Backward compatibility
        zoneIds: zoneIds,
        restaurantId: staffData.restaurant_id,
        isActive: staffData.is_active ?? true,
        receiptHeader: (staffData as any).receipt_header || null,
        receiptFooter: (staffData as any).receipt_footer || null,
        deletedAt: staffData.deleted_at,
        createdAt: staffData.created_at ?? new Date().toISOString(),
        updatedAt: staffData.updated_at ?? new Date().toISOString(),
        ...(restaurant && { restaurant }),
      }

      // Save to localStorage for route guards
      localStorage.setItem('digimataam_staff_auth', JSON.stringify(staff))

      return staff
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  })
}

/**
 * Staff login mutation
 */
export function useStaffLogin() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      username,
      password,
      restaurantId,
    }: {
      username: string
      password: string
      restaurantId: string
    }) => {
      // Get email from server-side function
      const { data: email, error: emailError } = await supabase.rpc(
        'get_staff_login_email' as any,
        {
          p_username: username,
          p_restaurant_id: restaurantId,
        }
      )

      if (emailError || !email) {
        throw new Error('Staff not found or inactive')
      }

      // Sign in with Supabase Auth
      const { data, error } = await supabase.auth.signInWithPassword({
        email: String(email),
        password,
      })

      if (error) {
        throw error
      }

      // Store restaurant ID for redirect
      localStorage.setItem('digimataam_staff_restaurant', restaurantId)

      return data
    },
    onSuccess: () => {
      // Invalidate queries to refetch staff data
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.staff() })
    },
  })
}

/**
 * Staff logout mutation
 */
export function useStaffLogout() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
    },
    onSuccess: () => {
      // Clear staff data from localStorage
      localStorage.removeItem('digimataam_staff_auth')
      // Clear all queries
      queryClient.clear()
    },
  })
}

/**
 * Check if user is authenticated staff
 */
export function useIsStaff() {
  const { data: staff, isLoading } = useCurrentStaff()
  return { isStaff: !!staff, isLoading }
}

/**
 * Get staff redirect URL based on stored restaurant ID
 */
export function getStaffRedirectUrl(): string {
  const restaurantId = localStorage.getItem('digimataam_staff_restaurant')
  if (restaurantId) {
    return `/staff-login?restaurant=${restaurantId}`
  }
  return '/'
}
